import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, ChevronRight, Minus, Plus } from 'lucide-react';
import { Card, Modal, PrimaryButton, TicketingShell } from '../../components/ticketing/TicketingShell';
import OrderSummary from '../../components/ticketing/OrderSummary';
import { formatMoney, inviteSearch, passCurrency, passSaleState, premiumHome, readCart, writeCart } from '../../utils/ticketing';

const SHOWN_BENEFITS = 3;

const QtyStepper = ({ value, max, onChange }) => (
  <div className="inline-flex items-center rounded-xl border bg-white">
    <button type="button" onClick={() => onChange(value - 1)} className="p-2.5 disabled:opacity-40" aria-label="Fewer attendees">
      <Minus className="h-4 w-4" />
    </button>
    <span className="w-8 text-center font-semibold tabular-nums" aria-live="polite">{value}</span>
    <button type="button" onClick={() => onChange(value + 1)} disabled={value >= max} className="p-2.5 disabled:opacity-40" aria-label="More attendees">
      <Plus className="h-4 w-4" />
    </button>
  </div>
);

const PassCard = ({ pass, currency, cart, onAdd, onRemove, onQty }) => {
  const [expanded, setExpanded] = useState(false);
  const state = passSaleState(pass);
  const inCart = cart?.passId === pass.id;
  const benefits = expanded ? pass.benefits : pass.benefits.slice(0, SHOWN_BENEFITS);
  const hidden = pass.benefits.length - SHOWN_BENEFITS;
  return (
    <Card className={`p-5 sm:p-6 ${inCart ? 'ring-2 ring-[var(--tk-primary)]' : ''}`}>
      <div className="flex gap-4">
        {pass.iconUrl && <img src={pass.iconUrl} alt="" className="h-14 w-14 shrink-0 rounded-full object-cover" />}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
            <h3 className="text-lg font-semibold">{pass.name}</h3>
            <div className="text-right">
              <p className="text-xl font-bold tabular-nums">{pass.type === 'PAID' ? formatMoney(pass.price, currency) : (pass.type === 'APPROVAL' ? 'On approval' : 'Free')}</p>
              {pass.type === 'PAID' && <p className="text-xs text-gray-500">Per person</p>}
            </div>
          </div>
          {pass.description && <p className="mt-1 text-sm text-gray-600">{pass.description}</p>}
          {benefits.length > 0 && (
            <ul className="mt-2 space-y-1.5">
              {benefits.map((benefit) => (
                <li key={benefit} className="flex gap-2 text-sm text-gray-700">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--tk-primary)]" />{benefit}
                </li>
              ))}
            </ul>
          )}
          {hidden > 0 && (
            <button type="button" onClick={() => setExpanded((v) => !v)} className="mt-2 rounded-full border border-[var(--tk-primary)] px-2.5 py-0.5 text-xs font-semibold text-[var(--tk-primary)]">
              {expanded ? 'Show less' : `+${hidden} more`}
            </button>
          )}
        </div>
      </div>
      <div className="mt-4 flex items-center justify-end gap-3 border-t pt-4">
        {state !== 'open' ? (
          <span className="text-sm font-medium text-gray-500">{state === 'upcoming' ? 'Opens soon' : 'Sales closed'}</span>
        ) : inCart ? (
          <>
            {pass.maxPerBooking > 1 && <QtyStepper value={cart.qty} max={pass.maxPerBooking} onChange={(qty) => (qty < 1 ? onRemove() : onQty(qty))} />}
            <button type="button" onClick={onRemove} className="rounded-lg border px-4 py-2 text-sm font-semibold">Remove</button>
          </>
        ) : (
          <button type="button" onClick={onAdd} className="rounded-lg bg-[var(--tk-primary)] px-5 py-2 text-sm font-semibold text-white hover:brightness-110">Add</button>
        )}
      </div>
    </Card>
  );
};

const instructionsKey = (eventId) => `tkInstructions:${eventId}`;

/** Passes of one group with an order summary; one pass type per booking. */
const PassPicker = ({ view, eventRef, groupId }) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const t = view.ticketing;
  const group = t.groups.find((g) => g.id === groupId);
  const passes = useMemo(() => t.passes.filter((pass) => pass.groupId === groupId), [t, groupId]);
  const [cart, setCart] = useState(() => {
    const saved = readCart(view.eventId);
    return saved && passes.some((p) => p.id === saved.passId) ? saved : null;
  });
  const [showInstructions, setShowInstructions] = useState(() => {
    try { return !!t.instructions && !sessionStorage.getItem(instructionsKey(view.eventId)); } catch (_) { return !!t.instructions; }
  });

  useEffect(() => { writeCart(view.eventId, cart); }, [view.eventId, cart]);

  const closeInstructions = () => {
    setShowInstructions(false);
    try { sessionStorage.setItem(instructionsKey(view.eventId), '1'); } catch (_) { /* storage unavailable */ }
  };

  const selected = passes.find((p) => p.id === cart?.passId);
  const currency = passCurrency(selected || passes[0], view);
  const back = premiumHome(eventRef, search);

  if (!group) {
    return (
      <TicketingShell view={view} back={back} title={view.name} subtitle={t.tagline}>
        <Card className="p-8 text-center text-gray-600">This category isn't available.</Card>
      </TicketingShell>
    );
  }

  return (
    <TicketingShell view={view} back={back} title={view.name} subtitle={t.tagline}>
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
        <div>
          <h1 className="text-3xl font-bold sm:text-4xl">Select your {group.name} category</h1>
          {group.description && <p className="mt-2 text-gray-600">{group.description}</p>}
          <p className="mt-4 text-gray-600">Choose a category to continue.</p>
          <span className="mt-3 block h-1 w-16 rounded bg-[var(--tk-accent)]" />
          <div className="mt-6 space-y-5">
            {passes.map((pass) => (
              <PassCard
                key={pass.id}
                pass={pass}
                currency={passCurrency(pass, view)}
                cart={cart}
                onAdd={() => setCart({ passId: pass.id, qty: 1 })}
                onRemove={() => setCart(null)}
                onQty={(qty) => setCart({ passId: pass.id, qty })}
              />
            ))}
          </div>
        </div>
        <OrderSummary
          pass={selected}
          qty={cart?.qty || 0}
          currency={currency}
          onRemove={() => setCart(null)}
          note={t.note}
          action={(
            <PrimaryButton className="w-full" disabled={!selected} onClick={() => navigate(`/register/${eventRef}/book/${selected.id}${inviteSearch(search)}`)}>
              Continue <ChevronRight className="h-5 w-5" />
            </PrimaryButton>
          )}
        />
      </div>
      <Modal
        open={showInstructions}
        onClose={closeInstructions}
        title={t.instructions?.title || 'General instructions'}
        footer={<div className="flex justify-center"><PrimaryButton onClick={closeInstructions} className="min-w-[140px]">Ok</PrimaryButton></div>}
      >
        <ul className="space-y-3 text-base text-gray-800">
          {(t.instructions?.items || []).map((item) => <li key={item} className="flex gap-2"><span>•</span>{item}</li>)}
        </ul>
      </Modal>
    </TicketingShell>
  );
};

export default PassPicker;
